// tbitmap.ts ↔ game/tbitmap.cpp + the TBitmap class (ht.h:1151+).
//
// In the shipped web client a TBitmap is an ATLAS REFERENCE, not a pixel decoder:
// atlas position/size (post-crop), original canvas size, crop offsets, the
// per-side atlas map, the z-sort baseline, and the optional team-color mask-decal
// coordinates. Pixels live in the pack's texture<N>.png atlases (TexAtlasMgr).
// Blt/Fill/Flash run through the renderer's DibBitmap seam (M2); the geometry
// here is everything the sim/UI layers consume.

import type { TexAtlasMgr, DibBitmapLike, Rect, Side } from './texatlasmgr.ts';
import { CpuDibBitmap } from './dibbitmap.ts';

export const ksideNeutral = 0; // ht.h Side — neutral side index
// TS-only sentinel "side" for the attack-move target marker: blit a RED-tinted copy that
// keeps the original alpha (mirrors the side -1 white-flash path). Lets the attack-move
// target reuse movetarget.anir but read as visually distinct, with no new art assets.
export const ksideAttackMoveTint = -2;

export class TBitmap {
  m_pszFn = '';
  m_x = 0;
  m_y = 0;
  m_cx = 0;
  m_cy = 0;
  m_cxOrig = 0;
  m_cyOrig = 0;
  m_ccLeft = 0;
  m_ccTop = 0;
  m_yBaseline = 0;
  m_anSideMap: number[] = [];
  m_fHasMask = false;
  m_fDegenerate = false;
  m_iatlasMask = 0;
  m_xMask = 0;
  m_yMask = 0;
  m_cxMask = 0;
  m_cyMask = 0;
  m_ccLeftMask = 0;
  m_ccTopMask = 0;

  private tam: TexAtlasMgr;

  constructor(tam: TexAtlasMgr) {
    this.tam = tam;
  }

  // game/tbitmap.cpp:34-50
  Init(
    pszFn: string,
    x: number,
    y: number,
    cx: number,
    cy: number,
    cxOrig: number,
    cyOrig: number,
    ccLeft: number,
    ccTop: number,
    yBaseline: number,
    anSideMap: number[],
  ): boolean {
    this.m_pszFn = pszFn;
    this.m_x = x;
    this.m_y = y;
    this.m_cx = cx;
    this.m_cy = cy;
    this.m_cxOrig = cxOrig;
    this.m_cyOrig = cyOrig;
    this.m_ccLeft = ccLeft;
    this.m_ccTop = ccTop;
    this.m_yBaseline = yBaseline;
    this.m_anSideMap = anSideMap;
    return true;
  }

  // ht.h TBitmap inline accessors
  GetTextureSize(): { cx: number; cy: number } {
    return { cx: this.m_cx, cy: this.m_cy };
  }
  GetSize(): { cx: number; cy: number } {
    return { cx: this.m_cxOrig, cy: this.m_cyOrig };
  }
  GetPosition(): { x: number; y: number } {
    return { x: this.m_x, y: this.m_y };
  }
  GetAtlas(side: Side): number {
    return this.m_anSideMap[side];
  }
  ClippedLeft(): number {
    return this.m_ccLeft;
  }
  ClippedTop(): number {
    return this.m_ccTop;
  }
  HasMask(): boolean {
    return this.m_fHasMask;
  }
  MaskAtlas(): number {
    return this.m_iatlasMask;
  }
  MaskX(): number {
    return this.m_xMask;
  }
  MaskY(): number {
    return this.m_yMask;
  }
  MaskCx(): number {
    return this.m_cxMask;
  }
  MaskCy(): number {
    return this.m_cyMask;
  }
  MaskClippedLeft(): number {
    return this.m_ccLeftMask;
  }
  MaskClippedTop(): number {
    return this.m_ccTopMask;
  }
  IsDegenerate(): boolean {
    return this.m_fDegenerate;
  }
  SetDegenerate(): void {
    this.m_fDegenerate = true;
  }

  SetMask(
    iatlas: number,
    x: number,
    y: number,
    cx: number,
    cy: number,
    ccLeft: number,
    ccTop: number,
  ): void {
    this.m_fHasMask = true;
    this.m_iatlasMask = iatlas;
    this.m_xMask = x;
    this.m_yMask = y;
    this.m_cxMask = cx;
    this.m_cyMask = cy;
    this.m_ccLeftMask = ccLeft;
    this.m_ccTopMask = ccTop;
  }

  // game/tbitmap.cpp:98-107 — the scenery z-sort baseline (sim-quirk #12 history)
  GetBaseline(): number {
    return this.m_yBaseline;
  }

  // game/tbitmap.cpp:52-73
  BltTo(
    pbmDst: DibBitmapLike,
    xDst: number,
    yDst: number,
    side: Side = ksideNeutral,
    prcSrc: Rect | null = null,
    opacity = 255,
  ): void {
    // side -1 = flash white (tbitmap.cpp:58-68): blit an all-white copy that
    // keeps the original alpha (the targeted-unit flash).
    if (side === -1) {
      const pbm = this.Flash();
      if (pbm !== null) pbmDst.Blt(pbm, prcSrc, xDst, yDst, opacity);
      return;
    }
    // ksideAttackMoveTint (-2): red-tinted copy preserving alpha (attack-move target marker).
    if (side === ksideAttackMoveTint) {
      const pbm = this.Tint(220, 32, 32);
      if (pbm !== null) pbmDst.Blt(pbm, prcSrc, xDst, yDst, opacity);
      return;
    }
    this.tam.BltTo(this, pbmDst, xDst, yDst, side, prcSrc, opacity);
  }

  // game/tbitmap.cpp:148-219 — an all-white copy preserving per-pixel alpha.
  // The C++ renders side 1 into a target texture and reads the pixels back;
  // here the CPU blit lands in a scratch dib directly. Allocated per call as
  // the C++ does (the flash lasts a handful of frames).
  Flash(): CpuDibBitmap | null {
    const pbm = new CpuDibBitmap(this.m_cxOrig, this.m_cyOrig);
    this.tam.BltTo(this, pbm, 0, 0, 1 /* ksidmSide1 (tbitmap.cpp:158) */, null);
    const px = pbm.rgba;
    for (let i = 0; i < px.length; i += 4) {
      if (px[i + 3] !== 0) {
        px[i] = 255;
        px[i + 1] = 255;
        px[i + 2] = 255;
      }
    }
    return pbm;
  }

  // TS-only: a solid-color copy preserving per-pixel alpha (same shape as Flash, used for
  // the attack-move target marker). Allocated per call like Flash (the marker is brief).
  // NB: copies via the NEUTRAL side (the side the marker is actually drawn with) — unlike
  // Flash's side 1, which only resolves for team-colored sprites; the movetarget marker has
  // no side-1 atlas entry, so side 1 would yield a blank copy (no marker at all).
  Tint(r: number, g: number, b: number): CpuDibBitmap | null {
    const pbm = new CpuDibBitmap(this.m_cxOrig, this.m_cyOrig);
    this.tam.BltTo(this, pbm, 0, 0, ksideNeutral, null);
    const px = pbm.rgba;
    for (let i = 0; i < px.length; i += 4) {
      if (px[i + 3] !== 0) {
        px[i] = r;
        px[i + 1] = g;
        px[i + 2] = b;
      }
    }
    return pbm;
  }

  // game/tbitmap.cpp:109-146 — tile-fill a destination box, clipping edge tiles
  FillTo(pbmDst: DibBitmapLike, xDst: number, yDst: number, cxDst: number, cyDst: number): void {
    for (let y = 0; y < cyDst; y++) {
      if (y % this.m_cy) continue;
      for (let x = 0; x < cxDst; x++) {
        if (x % this.m_cx) continue;
        let cxClip = cxDst - x;
        let cyClip = cyDst - y;
        if (cxClip < this.m_cxOrig || cyClip < this.m_cyOrig) {
          if (cxClip > this.m_cxOrig) cxClip = this.m_cxOrig;
          if (cyClip > this.m_cyOrig) cyClip = this.m_cyOrig;
          this.BltTo(pbmDst, xDst + x, yDst + y, ksideNeutral, {
            left: 0,
            top: 0,
            right: cxClip,
            bottom: cyClip,
          });
        } else {
          this.BltTo(pbmDst, xDst + x, yDst + y);
        }
      }
    }
  }
}
